// src/components/Colaborador/ControlePonto.js
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebaseConfig';
import { doc, onSnapshot, setDoc, Timestamp, arrayUnion } from 'firebase/firestore';
import { 
  FaPlay, 
  FaPause, 
  FaStop, 
  FaMugHot, 
  FaRegClock, 
  FaSignInAlt, 
  FaSignOutAlt, 
  FaHourglassHalf 
} from 'react-icons/fa';
import { CgCoffee } from 'react-icons/cg';

// Função para formatar milissegundos para HH:MM:SS
const formatarMsParaTempo = (ms) => {
  if (ms < 0) ms = 0;
  const totalSegundos = Math.floor(ms / 1000);
  const horas = String(Math.floor(totalSegundos / 3600)).padStart(2, '0');
  const minutos = String(Math.floor((totalSegundos % 3600) / 60)).padStart(2, '0');
  const segundos = String(totalSegundos % 60).padStart(2, '0');
  return `${horas}:${minutos}:${segundos}`;
};


const ControlePonto = ({ userId, empresaId }) => {
  const [statusPonto, setStatusPonto] = useState('fora'); // 'fora', 'trabalhando', 'pausa'
  const [registrosDoDia, setRegistrosDoDia] = useState([]);
  
  // Novos estados para os totais
  const [tempoTrabalhadoTotal, setTempoTrabalhadoTotal] = useState('00:00:00');
  const [tempoPausaTotal, setTempoPausaTotal] = useState('00:00:00');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const getHojeFormatado = () => new Date().toISOString().split('T')[0];

  // ==================================================================
  // NOVA LÓGICA DE CÁLCULO DE TEMPO
  // ==================================================================
  const calcularTempos = useCallback((registros) => {
    let totalTrabalhadoMs = 0;
    let totalPausaMs = 0;
    let ultimoTimestampEntrada = null;
    let ultimoTimestampPausa = null;

    registros.forEach(reg => {
      const timestampAtual = reg.timestamp.toDate();
      
      switch (reg.tipo) {
        case 'entrada':
        case 'fim_pausa':
          ultimoTimestampEntrada = timestampAtual;
          if (ultimoTimestampPausa) {
            totalPausaMs += (timestampAtual - ultimoTimestampPausa);
            ultimoTimestampPausa = null;
          }
          break;
        
        case 'inicio_pausa':
          if (ultimoTimestampEntrada) {
            totalTrabalhadoMs += (timestampAtual - ultimoTimestampEntrada);
            ultimoTimestampEntrada = null;
          }
          ultimoTimestampPausa = timestampAtual;
          break;

        case 'saida':
          if (ultimoTimestampEntrada) {
            totalTrabalhadoMs += (timestampAtual - ultimoTimestampEntrada);
            ultimoTimestampEntrada = null;
          }
          // Se a saída for registrada durante uma pausa, a pausa é finalizada sem adicionar ao tempo de trabalho.
          ultimoTimestampPausa = null; 
          break;
        
        default:
          break;
      }
    });

    // Retorna os totais calculados e os timestamps atuais para o timer em tempo real
    return { totalTrabalhadoMs, totalPausaMs, ultimoTimestampEntrada, ultimoTimestampPausa };
  }, []);

  // Hook principal que atualiza os timers e o status
  useEffect(() => {
    setLoading(true);
    const hojeId = getHojeFormatado();
    if (!userId || !empresaId) {
      setError("IDs de usuário ou empresa não fornecidos.");
      setLoading(false);
      return;
    }
    const docRef = doc(db, 'Empresas', empresaId, 'registrosPonto', userId, 'dias', hojeId);

    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      const registros = docSnap.exists() ? [...docSnap.data().registros].sort((a, b) => a.timestamp.seconds - b.timestamp.seconds) : [];
      setRegistrosDoDia(registros);
      
      const ultimoRegistro = registros.length > 0 ? registros[registros.length - 1] : null;
      let statusAtual = 'fora';
      if (ultimoRegistro) {
        switch (ultimoRegistro.tipo) {
          case 'entrada':
          case 'fim_pausa':
            statusAtual = 'trabalhando';
            break;
          case 'inicio_pausa':
            statusAtual = 'pausa';
            break;
          default:
            statusAtual = 'fora';
        }
      }
      setStatusPonto(statusAtual);
      setLoading(false);
    }, (err) => {
      console.error("Erro ao buscar registros de ponto: ", err);
      setError("Falha ao carregar os registros de ponto.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId, empresaId]);

  // Hook que roda o timer a cada segundo
  useEffect(() => {
    const { totalTrabalhadoMs, totalPausaMs, ultimoTimestampEntrada, ultimoTimestampPausa } = calcularTempos(registrosDoDia);

    const intervalId = setInterval(() => {
      let trabalhoAtualizado = totalTrabalhadoMs;
      let pausaAtualizada = totalPausaMs;
      
      if (statusPonto === 'trabalhando' && ultimoTimestampEntrada) {
        trabalhoAtualizado += (new Date() - ultimoTimestampEntrada);
      }
      
      if (statusPonto === 'pausa' && ultimoTimestampPausa) {
        pausaAtualizada += (new Date() - ultimoTimestampPausa);
      }

      setTempoTrabalhadoTotal(formatarMsParaTempo(trabalhoAtualizado));
      setTempoPausaTotal(formatarMsParaTempo(pausaAtualizada));
    }, 1000);

    return () => clearInterval(intervalId);
  }, [registrosDoDia, statusPonto, calcularTempos]);
  
  // ==================================================================

  const handleRegistrarPonto = async (tipo) => {
    if (!userId || !empresaId) return;
    setError('');
    setActionLoading(true);

    const hojeId = getHojeFormatado();
    const docRef = doc(db, 'Empresas', empresaId, 'registrosPonto', userId, 'dias', hojeId);
    const novoRegistro = { tipo, timestamp: Timestamp.now() };

    try {
      await setDoc(docRef, {
        registros: arrayUnion(novoRegistro)
      }, { merge: true });
    } catch (err) {
      console.error("Erro ao registrar ponto: ", err);
      setError("Não foi possível registrar o ponto. Tente novamente.");
    } finally {
      setActionLoading(false);
    }
  };
  
  const statusInfo = {
    fora: { texto: "Fora do Expediente", classe: 'fora', icone: <FaRegClock/> },
    trabalhando: { texto: "Trabalhando", classe: 'trabalhando', icone: <FaHourglassHalf/> },
    pausa: { texto: "Em Pausa", classe: 'pausa', icone: <CgCoffee/> },
  };

  const registroIcones = {
    entrada: <FaSignInAlt className="icon-entrada" />,
    inicio_pausa: <FaMugHot className="icon-pausa" />,
    fim_pausa: <FaPlay className="icon-fim-pausa" />,
    saida: <FaSignOutAlt className="icon-saida" />,
  };
  
  if (loading) {
    return <p className="loading-sm">Carregando informações de ponto...</p>;
  }

  return (
    <div className="controle-ponto-container-v2">
      {error && <p className="feedback-message error small">{error}</p>}
      
      <div className={`ponto-status-card status-${statusInfo[statusPonto].classe}`}>
        <div className="status-icon-wrapper">{statusInfo[statusPonto].icone}</div>
        <div className="status-details">
          <p className="status-texto">{statusInfo[statusPonto].texto}</p>
          {/* O TIMER PRINCIPAL AGORA MOSTRA O TEMPO TRABALHADO TOTAL */}
          <p className="ponto-timer-v2">{tempoTrabalhadoTotal}</p>
        </div>
      </div>

      <div className="ponto-actions-v2">
        {actionLoading ? <p className="loading-sm">Processando...</p> : (
            statusPonto === 'fora' ? (
              <button onClick={() => handleRegistrarPonto('entrada')} className="btn btn-primary btn-block submit-humor-btn mt-3" disabled={actionLoading}>
                <FaPlay /> Registrar Entrada
              </button>
            ) : statusPonto === 'trabalhando' ? (
              <div className="botoes-grupo">
                <button onClick={() => handleRegistrarPonto('inicio_pausa')} className="btn-ponto btn-pausa" disabled={actionLoading}>
                  <FaMugHot /> Iniciar Pausa
                </button>
                <button onClick={() => handleRegistrarPonto('saida')} className="btn-ponto btn-saida" disabled={actionLoading}>
                  <FaStop /> Registrar Saída
                </button>
              </div>
            ) : ( // 'pausa'
              <button onClick={() => handleRegistrarPonto('fim_pausa')} className="btn-ponto btn-retorno" disabled={actionLoading}>
                <FaPlay /> Retornar ao Trabalho
              </button>
            )
        )}
      </div>

      {/* NOVO BLOCO PARA EXIBIR O TOTAL DE PAUSA */}
      <div className="ponto-resumo-dia">
        <div className="resumo-item">
          <span>Total Trabalhado</span>
          <strong>{tempoTrabalhadoTotal}</strong>
        </div>
        <div className="resumo-item">
          <span>Tempo em Pausa</span>
          <strong>{tempoPausaTotal}</strong>
        </div>
      </div>

      <div className="ponto-historico-v2">
        <h6>REGISTROS DE HOJE</h6>
        {registrosDoDia.length === 0 ? (
          <p className="text-muted small text-center">Nenhum registro encontrado para hoje.</p>
        ) : (
          <ul className="ponto-historico-lista-v2">
            {registrosDoDia.map((reg, index) => (
              <li key={index}>
                <div className="registro-info">
                  <span className="registro-icone">{registroIcones[reg.tipo]}</span>
                  <span className="tipo-registro">{reg.tipo.replace('_', ' de ')}</span>
                </div>
                <span className="hora-registro">
                  {reg.timestamp.toDate().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ControlePonto;